import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  Phone, 
  MapPin, 
  Clock, 
  Heart,
  ArrowRight,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';
import { useBusinessInfo } from '../../hooks/useBusinessInfo';
import { useSettings } from '../../contexts/SettingsContext';
import axios from 'axios';

/**
 * Home Page Hero Section Component
 * Configurable hero section for the home page with options to show/hide elements
 */
const HomeHeroComponent = ({ config = {} }) => {
  const { businessInfo: currentBusinessInfo } = useBusinessInfo();
  const { urgent_care_enabled } = useSettings();
  const [currentHeroImage, setCurrentHeroImage] = useState(0);
  const [heroImages, setHeroImages] = useState([]);
  const [heroImagesLoading, setHeroImagesLoading] = useState(true);
  const [hospitalHours, setHospitalHours] = useState(null);
  const [urgentCareHours, setUrgentCareHours] = useState(null);
  const [hoursLoading, setHoursLoading] = useState(true);
  const [businessTimezone, setBusinessTimezone] = useState(null);

  const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

  // Configuration with defaults
  const layout = config.layout || 'split'; // 'split' or 'full_width'
  const showBadge = config.show_badge !== false;
  const badgeText = config.badge_text || 'Where Every Paw Finds Care';
  const mainTitle = config.main_title || 'Compassionate Care for Your';
  const mainTitleHighlight = config.main_title_highlight || 'Beloved Pets';
  const description = config.description || 'Quality veterinary care and specialty care services for dogs and cats. Modern facilities and experienced veterinarians to serve all pet owners throughout Northern Virginia.';
  
  // Slider image selection and text overlay options
  const selectedSliderImages = config.selected_slider_images || []; // Array of slider image IDs
  const showSliderText = config.show_slider_text !== false; // Show title/description overlay on images
  
  // Show/Hide options
  const showUrgentCareHours = config.show_urgent_care_hours !== false && urgent_care_enabled;
  const showGeneralHours = config.show_general_hours !== false;
  const showCallButton = config.show_call_button !== false;
  const showCheckInButton = config.show_checkin_button !== false && urgent_care_enabled;
  const showSliderDots = config.show_slider_dots !== false;
  const showSliderArrows = config.show_slider_arrows !== false;

  const primaryColor = 'var(--theme-primary)';
  const primaryLight = 'var(--theme-primary-light)';
  const primaryBg = 'var(--theme-primary-light)';

  // Fetch slider images from API
  useEffect(() => {
    const fetchSliderImages = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/api/slider-images`);
        if (response.data && response.data.slider_images) {
          let sortedImages = response.data.slider_images.sort((a, b) => (a.order || 0) - (b.order || 0));
          
          // Filter to only selected images if specified
          if (selectedSliderImages.length > 0) {
            sortedImages = sortedImages.filter(img => selectedSliderImages.includes(img.id));
          }
          
          setHeroImages(sortedImages);
        }
        setHeroImagesLoading(false);
      } catch (err) {
        console.error('Error fetching slider images:', err);
        setHeroImages([]);
        setHeroImagesLoading(false);
      }
    };

    fetchSliderImages();
  }, [API_BASE_URL, selectedSliderImages]);

  // Auto-rotate hero images
  useEffect(() => {
    if (heroImages.length > 1) {
      const interval = setInterval(() => {
        setCurrentHeroImage((prev) => (prev + 1) % heroImages.length);
      }, 5000);
      return () => clearInterval(interval);
    }
  }, [heroImages]);

  // Fetch hours from API
  useEffect(() => {
    const fetchHours = async () => {
      try {
        const [hospitalResponse, urgentResponse, businessResponse] = await Promise.all([
          fetch(`${API_BASE_URL}/api/hospital-hours`),
          fetch(`${API_BASE_URL}/api/urgent-care-hours`),
          fetch(`${API_BASE_URL}/api/business-info`)
        ]);

        if (hospitalResponse.ok) {
          const data = await hospitalResponse.json();
          setHospitalHours(data.hours || {});
        }

        if (urgentResponse.ok) {
          const data = await urgentResponse.json();
          setUrgentCareHours(data.hours || {});
        }

        if (businessResponse.ok) {
          const data = await businessResponse.json();
          setBusinessTimezone(data.timezone || 'America/New_York');
        }
      } catch (error) {
        console.error('Error fetching hours:', error);
      } finally {
        setHoursLoading(false);
      }
    };

    fetchHours();
  }, [API_BASE_URL]);

  // Helper functions for hours display
  const getCurrentBusinessDate = () => {
    if (!businessTimezone) return new Date();
    return new Date(new Date().toLocaleString('en-US', { timeZone: businessTimezone }));
  };

  const getCurrentDay = () => {
    const days = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    return days[getCurrentBusinessDate().getDay()];
  };

  const formatTime = (time) => {
    if (!time) return '';
    const [hours, minutes] = time.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour > 12 ? hour - 12 : hour === 0 ? 12 : hour;
    return `${displayHour}:${minutes} ${ampm}`;
  };

  const isCurrentlyOpen = (dayData) => {
    if (!dayData?.is_open) return false;
    const now = getCurrentBusinessDate();
    const currentTime = now.getHours() * 60 + now.getMinutes();
    const [openHour, openMin] = dayData.open_time.split(':').map(Number);
    const [closeHour, closeMin] = dayData.close_time.split(':').map(Number);
    const openTime = openHour * 60 + openMin;
    const closeTime = closeHour * 60 + closeMin;
    return currentTime >= openTime && currentTime < closeTime;
  };

  const getHoursStatus = (hoursData, type) => {
    if (!hoursData || hoursLoading) {
      return type === 'urgent' ? '3 PM - 10 PM Daily' : '9:00 AM - 6:00 PM Today';
    }

    const currentDay = getCurrentDay();
    const todayHours = hoursData[currentDay];

    if (!todayHours?.is_open) {
      return 'Closed today';
    }

    if (isCurrentlyOpen(todayHours)) {
      return `Open now • Closes at ${formatTime(todayHours.close_time)}`;
    }

    return `${formatTime(todayHours.open_time)} - ${formatTime(todayHours.close_time)} Today`;
  };

  // Render Full Width Layout
  if (layout === 'full_width') {
    return (
      <>
        <section className="relative w-full" style={{ height: '500px' }}>
          <div className="absolute inset-0">
            {heroImagesLoading ? (
              <div className="w-full h-full bg-gray-100 flex items-center justify-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2" style={{ borderColor: primaryColor }}></div>
              </div>
            ) : heroImages.length > 0 ? (
              <>
                {heroImages.map((image, index) => (
                  <div
                    key={image.id}
                    className={`absolute inset-0 transition-all duration-1000 ease-in-out ${
                      index === currentHeroImage ? 'opacity-100' : 'opacity-0'
                    }`}
                  >
                    <img
                      src={image.image_url}
                      alt={image.title}
                      className="w-full h-full object-cover"
                    />
                  </div>
                ))}
                
                <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/60"></div>
                
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-full px-6 sm:px-12 text-center text-white">
                    {/* Show slider image text overlay if enabled */}
                    {showSliderText && heroImages[currentHeroImage]?.title && (
                      <div className="mb-6">
                        <h2 className="text-xl md:text-2xl font-bold mb-2">
                          {heroImages[currentHeroImage].title}
                        </h2>
                        {heroImages[currentHeroImage].description && (
                          <p className="text-sm md:text-base opacity-90">
                            {heroImages[currentHeroImage].description}
                          </p>
                        )}
                      </div>
                    )}
                    
                    {/* Main hero content */}
                    {showBadge && (
                      <div className="inline-flex items-center px-4 py-2 rounded-full text-sm font-medium bg-white/20 backdrop-blur-sm mb-4">
                        <Heart className="h-4 w-4 mr-2" />
                        {badgeText}
                      </div>
                    )}
                    <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold mb-3 leading-tight">
                      {mainTitle}
                      <span className="block" style={{ color: 'var(--theme-secondary)' }}>{mainTitleHighlight}</span>
                    </h1>
                    <p className="text-sm md:text-base opacity-90 mb-5 max-w-xl mx-auto">
                      {description}
                    </p>
                    
                    {/* CTA Buttons */}
                    <div className="flex flex-col sm:flex-row gap-3 justify-center">
                      {showCheckInButton && (
                        <Link
                          to="/urgent-care-booking"
                          className="inline-flex items-center justify-center px-5 py-2.5 rounded-lg font-semibold transition-all duration-200 bg-white hover:bg-gray-100 text-sm"
                          style={{ color: primaryColor }}
                        >
                          Check In Online
                          <ArrowRight className="ml-2 h-4 w-4" />
                        </Link>
                      )}
                      {showCallButton && (
                        <a
                          href={`tel:${currentBusinessInfo.phone}`}
                          className="inline-flex items-center justify-center text-white px-5 py-2.5 rounded-lg font-semibold transition-all duration-200 shadow-lg text-sm"
                          style={{ backgroundColor: primaryColor }}
                        >
                          {currentBusinessInfo.phone}
                          <Phone className="ml-2 h-4 w-4" />
                        </a>
                      )}
                    </div>
                  </div>
                </div>
                
                {/* Slider Navigation Dots */}
                {showSliderDots && heroImages.length > 1 && (
                  <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex gap-2">
                    {heroImages.map((_, index) => (
                      <button
                        key={index}
                        onClick={() => setCurrentHeroImage(index)}
                        className={`w-2.5 h-2.5 rounded-full transition-all duration-300 ${
                          index === currentHeroImage ? 'bg-white w-6' : 'bg-white/50 hover:bg-white/75'
                        }`}
                      />
                    ))}
                  </div>
                )}
                
                {/* Navigation Arrows */}
                {showSliderArrows && heroImages.length > 1 && (
                  <>
                    <button
                      onClick={() => setCurrentHeroImage((prev) => (prev - 1 + heroImages.length) % heroImages.length)}
                      className="absolute left-3 top-1/2 -translate-y-1/2 p-2 rounded-full bg-black/30 hover:bg-black/50 text-white transition-all"
                    >
                      <ChevronLeft className="h-5 w-5" />
                    </button>
                    <button
                      onClick={() => setCurrentHeroImage((prev) => (prev + 1) % heroImages.length)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 p-2 rounded-full bg-black/30 hover:bg-black/50 text-white transition-all"
                    >
                      <ChevronRight className="h-5 w-5" />
                    </button>
                  </>
                )}
              </>
            ) : (
              <div className="w-full h-full bg-gradient-to-br from-gray-800 to-gray-900 flex items-center justify-center">
                <div className="text-center text-white">
                  <h3 className="text-xl font-bold mb-2">{mainTitle}</h3>
                  <p>{mainTitleHighlight}</p>
                </div>
              </div>
            )}
          </div>
        </section>
        
        {/* Quick Info Bar */}
        {(showUrgentCareHours || showGeneralHours || showCallButton) && (
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 -mt-8 relative z-10">
            <div className="bg-white shadow-lg rounded-xl p-4">
              <div className={`grid grid-cols-1 md:grid-cols-${showUrgentCareHours && showGeneralHours && showCallButton ? '3' : showUrgentCareHours || showGeneralHours ? '2' : '1'} gap-4`}>
                {showUrgentCareHours && (
                  <div className="flex items-center gap-3 justify-center md:justify-start">
                    <Clock className="h-5 w-5" style={{ color: primaryColor }} />
                    <div>
                      <p className="font-semibold text-gray-900 text-sm">Urgent Care</p>
                      <p className="text-xs text-gray-600">
                        {hoursLoading ? 'Loading...' : getHoursStatus(urgentCareHours, 'urgent')}
                      </p>
                    </div>
                  </div>
                )}
                {showGeneralHours && (
                  <div className="flex items-center gap-3 justify-center">
                    <MapPin className="h-5 w-5" style={{ color: primaryColor }} />
                    <div>
                      <p className="font-semibold text-gray-900 text-sm">General Practice</p>
                      <p className="text-xs text-gray-600">
                        {hoursLoading ? 'Loading...' : getHoursStatus(hospitalHours, 'general')}
                      </p>
                    </div>
                  </div>
                )}
                {showCallButton && (
                  <div className="flex items-center gap-3 justify-center md:justify-end">
                    <Phone className="h-5 w-5" style={{ color: primaryColor }} />
                    <div>
                      <p className="font-semibold text-gray-900 text-sm">Call Us</p>
                      <p className="text-xs text-gray-600">{currentBusinessInfo.phone}</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </>
    );
  }

  // Render Split Layout (Default)
  return (
    <section className="relative overflow-hidden" style={{ background: `linear-gradient(135deg, ${primaryBg} 0%, #ffffff 50%, #f0fdff 100%)` }}>
      <div className="absolute inset-0 bg-white/40"></div>
      <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-4" style={{ paddingBottom: '20px' }}>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
          {/* Left Content */}
          <div className="space-y-4">
            <div className="space-y-4">
              {showBadge && (
                <div className="inline-flex items-center px-4 py-2 rounded-full text-sm font-medium" style={{ backgroundColor: primaryBg, color: primaryColor }}>
                  <Heart className="h-4 w-4 mr-2" />
                  {badgeText}
                </div>
              )}
              <h1 className="font-bold text-gray-900 leading-tight" style={{ fontSize: '2rem' }}>
                {mainTitle}
                <span className="block" style={{ color: primaryColor }}>{mainTitleHighlight}</span>
              </h1>
              <p className="text-gray-600 leading-relaxed" style={{ fontSize: '1rem' }}>
                {description}
              </p>
            </div>

            {/* Info and CTA Compartments */}
            {(showUrgentCareHours || showGeneralHours) && (
              <div className={`grid grid-cols-1 ${showUrgentCareHours && showGeneralHours ? 'sm:grid-cols-2' : ''} gap-6`}>
                {/* Urgent Care Compartment */}
                {showUrgentCareHours && (
                  <div className="flex flex-col h-full">
                    <div className="flex items-start space-x-3 bg-white/60 backdrop-blur-sm py-4 pr-4 pl-0 rounded-lg h-20 mb-4 flex-shrink-0">
                      <Clock className="h-5 w-5 mt-1" style={{ color: primaryColor }} />
                      <div className="text-left flex-1">
                        <p className="font-semibold text-gray-900">Urgent Care</p>
                        <p className="text-sm text-gray-600 leading-tight">
                          {hoursLoading ? (
                            <span className="animate-pulse">Loading hours...</span>
                          ) : (
                            getHoursStatus(urgentCareHours, 'urgent')
                          )}
                        </p>
                      </div>
                    </div>
                    {showCheckInButton && (
                      <div className="flex-1 flex items-end">
                        <Link
                          to="/urgent-care-booking"
                          className="inline-flex items-center justify-center border-2 px-8 py-4 rounded-lg font-semibold transition-all duration-200 w-full"
                          style={{ 
                            borderColor: primaryColor, 
                            color: primaryColor,
                            height: '56px'
                          }}
                          onMouseEnter={(e) => {
                            e.target.style.backgroundColor = primaryColor;
                            e.target.style.color = 'white';
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.backgroundColor = 'transparent';
                            e.target.style.color = primaryColor;
                          }}
                        >
                          Check In Online
                          <ArrowRight className="ml-2 h-5 w-5" />
                        </Link>
                      </div>
                    )}
                  </div>
                )}

                {/* General Practice Compartment */}
                {showGeneralHours && (
                  <div className="flex flex-col h-full">
                    <div className="flex items-start space-x-3 bg-white/60 backdrop-blur-sm py-4 pr-4 pl-0 rounded-lg h-20 mb-4 flex-shrink-0">
                      <MapPin className="h-5 w-5 mt-1" style={{ color: primaryColor }} />
                      <div className="text-left flex-1">
                        <p className="font-semibold text-gray-900">General Practice</p>
                        <p className="text-sm text-gray-600 leading-tight">
                          {hoursLoading ? (
                            <span className="animate-pulse">Loading hours...</span>
                          ) : (
                            getHoursStatus(hospitalHours, 'general')
                          )}
                        </p>
                      </div>
                    </div>
                    {showCallButton && (
                      <div className="flex-1 flex items-end">
                        <a
                          href={`tel:${currentBusinessInfo.phone}`}
                          className="inline-flex items-center justify-center text-white px-8 py-4 rounded-lg font-semibold transition-all duration-200 shadow-lg hover:shadow-xl group w-full"
                          style={{ backgroundColor: primaryColor, height: '56px' }}
                          onMouseEnter={(e) => e.target.style.backgroundColor = 'var(--theme-primary-hover)'}
                          onMouseLeave={(e) => e.target.style.backgroundColor = primaryColor}
                        >
                          {currentBusinessInfo.phone}
                          <Phone className="ml-2 h-5 w-5 group-hover:scale-110 transition-transform" />
                        </a>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Right Content - Hero Image */}
          <div className="relative">
            {heroImagesLoading ? (
              <div className="relative rounded-lg overflow-hidden shadow-2xl bg-gray-100 flex items-center justify-center" style={{ height: '25rem' }}>
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
              </div>
            ) : heroImages.length > 0 ? (
              <>
                <div className="relative rounded-lg overflow-hidden shadow-2xl">
                  <div className="relative w-full" style={{ height: '25rem' }}>
                    {heroImages.map((image, index) => (
                      <div
                        key={image.id}
                        className={`absolute inset-0 transition-all duration-1000 ease-in-out transform ${
                          index === currentHeroImage 
                            ? 'opacity-100 scale-100 translate-x-0' 
                            : 'opacity-0 scale-105 translate-x-4'
                        }`}
                      >
                        <img
                          src={image.image_url}
                          alt={image.title}
                          className="w-full h-full object-cover"
                        />
                      </div>
                    ))}
                    
                    {/* Text overlay for slider images if enabled */}
                    {showSliderText && heroImages[currentHeroImage]?.title && (
                      <>
                        {/* Dark gradient overlay for better text readability */}
                        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/40 to-transparent"></div>
                        
                        {/* Text content */}
                        <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
                          <h3 className="text-lg md:text-xl font-bold mb-2">
                            {heroImages[currentHeroImage].title}
                          </h3>
                          {heroImages[currentHeroImage].description && (
                            <p className="text-sm md:text-base opacity-90 line-clamp-2">
                              {heroImages[currentHeroImage].description}
                            </p>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                </div>

                {/* Slider Controls */}
                {heroImages.length > 1 && (
                  <>
                    {showSliderDots && (
                      <div className="flex justify-center gap-2 mt-4">
                        {heroImages.map((_, index) => (
                          <button
                            key={index}
                            onClick={() => setCurrentHeroImage(index)}
                            className={`w-2.5 h-2.5 rounded-full transition-all duration-300 ${
                              index === currentHeroImage 
                                ? 'w-6' 
                                : 'hover:bg-gray-400'
                            }`}
                            style={{
                              backgroundColor: index === currentHeroImage ? primaryColor : '#D1D5DB'
                            }}
                          />
                        ))}
                      </div>
                    )}
                  </>
                )}
              </>
            ) : null}
          </div>
        </div>
      </div>
    </section>
  );
};

export default HomeHeroComponent;
